import React, { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getTokens, removeTokens } from '@/generated/client/token/token';
import { MinimalToken } from '@/generated/model';
import { fulldate } from '@/utilities/dateUtils';
import { TFunction } from 'i18next';

import './ShareableLinksPopup.css';
import { copyClipboard, formatLink } from '@/utilities/linkUtils';
import { Close, DeleteForever, FilterAltOutlined } from '@mui/icons-material';
import { useNotificationStore } from '@/stores/notificationstore';
import { useUserPreferencesState } from '@/hooks/useUserPreferencesState';
import {
  DEFAULT_SHAREABLE_LINKS_PREFERENCES,
  SHAREABLE_LINKS_PREFERENCES_KEY,
  updateShowInactivePreference,
} from './shareableLinksPreferences';
import {
  dismissActiveStateAlert,
  shouldShowActiveStateAlert,
} from '@/components/shareableLinks/activeStateAlertSession';
import CustomDrawer from '@/components/common/CustomDrawer';
import { Popup } from '@/components/common/Popup';

type GroupedTokens = {
  targetName: string;
  targetTokens: MinimalToken[];
};

function mapTokensByLicensePlate(tokens: MinimalToken[]): GroupedTokens[] {
  const grouped = tokens.reduce<Record<string, GroupedTokens>>((tokensByTarget, token) => {
    const licensePlate = token.targetName;

    if (!tokensByTarget[licensePlate]) {
      tokensByTarget[licensePlate] = {
        targetName: licensePlate,
        targetTokens: [],
      };
    }

    tokensByTarget[licensePlate].targetTokens.push(token);

    return tokensByTarget;
  }, {});

  return Object.values(grouped).sort((a, b) =>
    a.targetName.localeCompare(b.targetName, undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  );
}

async function loadTokenGroups(showInactive: boolean) {
  const request = {
    offset: 0,
    limit: 10000,
    showInactive: showInactive,
  };

  const response = await getTokens(request);
  return mapTokensByLicensePlate(response.data || []);
}

function FilterPanel({
  open,
  anchorEl,
  handleCloseFilter,
  showInactive,
  setShowInactive,
  isPhone,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  handleCloseFilter: () => void;
  showInactive: boolean;
  setShowInactive: (showInactive: boolean) => void;
  isPhone: boolean;
}) {
  const { t } = useTranslation();

  function handleShowInactiveChange(showInactive: boolean) {
    setShowInactive(showInactive);
    handleCloseFilter();
  }

  if (isPhone) {
    return (
      <CustomDrawer isOpen={open} onClose={handleCloseFilter}>
        <Box className="shareable-filter-drawer">
          <Typography variant="h6" className="shareable-filter-title">
            {t('shareableLinks.filter')}
          </Typography>

          <Divider />

          <FormControlLabel
            className="shareable-filter-option"
            control={
              <Checkbox
                className={showInactive ? 'filter-checkbox' : ''}
                checked={showInactive}
                onChange={(e) => handleShowInactiveChange(e.target.checked)}
              />
            }
            label={t('shareableLinks.showInactive')}
          />
        </Box>
      </CustomDrawer>
    );
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleCloseFilter}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <div className="filter">
        <FormControlLabel
          control={
            <Checkbox
              className={showInactive ? 'filter-checkbox' : ''}
              checked={showInactive}
              onChange={(e) => {
                handleShowInactiveChange(e.target.checked);
              }}
            />
          }
          label={t('shareableLinks.showInactive')}
        />
      </div>
    </Popover>
  );
}

function EmptyState({ isPhone, showInactive }: { isPhone: boolean; showInactive: boolean }) {
  const { t } = useTranslation();
  return (
    <Box className="empty-coordinate-links">
      <img src="svg/token-empty-state.svg" alt="no visible links" />

      <Typography variant={isPhone ? 'subtitle1' : 'h6'} className="empty-coordinate-links-title">
        {t(showInactive ? 'shareableLinks.emptyStateTitle' : 'shareableLinks.emptyStateTitleActiveOnly')}
      </Typography>

      <Typography variant={isPhone ? 'body2' : 'body1'} className="empty-coordinate-links-description">
        {t('shareableLinks.emptyStateDescription1')}
        <RouterLink to="/tracking/select" className="empty-coordinate-links-link">
          {t('shareableLinks.emptyStateLink')}
        </RouterLink>
        {t('shareableLinks.emptyStateDescription2')}
        <br />
        {t('shareableLinks.emptyStateDescription3')}
      </Typography>
    </Box>
  );
}

function DeleteDialog({
  openDeleteDialog,
  handleCloseDialog,
  handleConfirmDelete,
  isPhone,
}: {
  openDeleteDialog: boolean;
  handleCloseDialog: () => void;
  handleConfirmDelete: () => void;
  isPhone: boolean;
}) {
  const { t } = useTranslation();
  const deleteQuestionParagraphs = t('shareableLinks.deleteTokenQuestion').split('\n');

  return (
    <Dialog
      open={openDeleteDialog}
      onClose={handleCloseDialog}
      slotProps={{
        paper: {
          className: isPhone ? 'shareable-delete-dialog-paper phone' : 'shareable-delete-dialog-paper',
        },
      }}
    >
      <DialogTitle>{t('shareableLinks.deleteTokenTitle')}</DialogTitle>

      <DialogContent>
        <DialogContentText component="div" className="shareable-delete-dialog-content">
          {deleteQuestionParagraphs.map((paragraph) => (
            <Typography key={paragraph} variant="body2" component="p">
              {paragraph}
            </Typography>
          ))}
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseDialog}>{t('general.cancel')}</Button>

        <Button onClick={handleConfirmDelete} color="error" variant="contained">
          {t('shareableLinks.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ActiveStateAlert({
  showInactive,
  setShowInactive,
  onClose,
  isPhone,
}: {
  showInactive: boolean;
  setShowInactive: (showInactive: boolean) => void;
  onClose: () => void;
  isPhone: boolean;
}) {
  const { t } = useTranslation();

  if (!isPhone) {
    return (
      <Alert
        severity={showInactive ? 'info' : 'warning'}
        className="inactive-alert"
        action={
          <div className="inactive-alert-actions">
            <Button color="inherit" size="small" variant="text" onClick={() => setShowInactive(!showInactive)}>
              {showInactive ? t('shareableLinks.hideInactive') : t('shareableLinks.showInactive')}
            </Button>

            <IconButton size="small" color="inherit" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </div>
        }
      >
        <AlertTitle>
          {showInactive ? t('shareableLinks.inactiveAlertTitle') : t('shareableLinks.activeAlertTitle')}
        </AlertTitle>

        {showInactive ? t('shareableLinks.inactiveAlertContent') : t('shareableLinks.activeAlertContent')}
      </Alert>
    );
  }

  return (
    <Alert
      severity={showInactive ? 'info' : 'warning'}
      className="inactive-alert inactive-alert-mobile"
      icon={false}
      action={
        <IconButton size="small" color="inherit" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      }
    >
      <Typography component="div" variant="subtitle2" className="inactive-alert-title">
        {showInactive ? t('shareableLinks.inactiveAlertTitle') : t('shareableLinks.activeAlertTitle')}
      </Typography>

      <Typography component="div" variant="caption">
        {showInactive ? t('shareableLinks.inactiveAlertContent') : t('shareableLinks.activeAlertContent')}
      </Typography>
    </Alert>
  );
}

export default function ShareableLinksPopup({
  closePopup,
  isBottom,
}: {
  closePopup: () => Promise<void>;
  isBottom: boolean;
}) {
  const { t, i18n } = useTranslation();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const [groups, setGroups] = useState<GroupedTokens[]>();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedTokenId, setSelectedTokenId] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [preferences, setPreferences] = useUserPreferencesState(
    SHAREABLE_LINKS_PREFERENCES_KEY,
    DEFAULT_SHAREABLE_LINKS_PREFERENCES
  );
  const showInactive = preferences.showInactive;
  const [showAlert, setShowAlert] = useState(() => shouldShowActiveStateAlert(showInactive));
  const open = Boolean(anchorEl);
  const isPhone = isBottom;

  function handleOpenFilter(event: MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleCloseFilter() {
    setAnchorEl(null);
  }

  useEffect(() => {
    let ignore = false;

    void loadTokenGroups(showInactive)
      .then((tokenGroups) => {
        if (!ignore) setGroups(tokenGroups);
      })
      .catch((error) => {
        if (!ignore) {
          console.error(error);
          setGroups([]);
        }
      });

    return () => {
      ignore = true;
    };
  }, [showInactive]);

  async function copyToClipboard(url: string) {
    const copied = await copyClipboard(url);
    const timeout = 5;
    if (copied) {
      const message = t('shareableLinks.copiedToClipboard') + '\n' + url;
      showNotification('success', message, undefined, timeout);
    } else {
      const message = t('shareableLinks.failedToCopy');
      showNotification('error', message, undefined, timeout);
    }
  }

  function handleDeleteClick(tokenId: number) {
    setSelectedTokenId(tokenId);
    setOpenDeleteDialog(true);
  }

  async function handleConfirmDelete() {
    if (selectedTokenId == null) return;

    const request = {
      tokenIds: [selectedTokenId],
    };
    try {
      await removeTokens(request);
      const message = t('shareableLinks.deleteSuccess');
      showNotification('success', message, undefined, 5);
      setGroups(await loadTokenGroups(showInactive));
    } catch {
      const message = t('shareableLinks.deleteFailed');
      showNotification('error', message, undefined, 5);
    } finally {
      setOpenDeleteDialog(false);
      setSelectedTokenId(null);
    }
  }

  function handleCloseDialog() {
    setOpenDeleteDialog(false);
    setSelectedTokenId(null);
  }

  function handleCloseActiveStateAlert() {
    dismissActiveStateAlert(showInactive);
    setShowAlert(false);
  }

  function handleShowInactiveChange(nextShowInactive: boolean) {
    if (nextShowInactive === showInactive) return;

    setGroups(undefined);
    setPreferences((currentPreferences) => updateShowInactivePreference(currentPreferences, nextShowInactive));
    setShowAlert(true);
  }

  function ShareLinkRow({ token, t }: { token: MinimalToken; t: TFunction }) {
    const url = formatLink(token.token, i18n);
    return (
      <Box className="token-grid">
        <Box className={`details-column ${!token.isValid ? 'invalid-token' : ''}`}>
          <Typography variant="caption" color="text.secondary">
            {t('shareableLinks.created')} {fulldate(token.creationDateMicros, t)}
          </Typography>

          <Typography variant="body2" color="text.primary">
            {fulldate(token.fromMicros, t)} - {fulldate(token.toMicros, t)}
          </Typography>

          {token.comment && (
            <Typography variant="body2" color="text.secondary">
              {token.comment}
            </Typography>
          )}
        </Box>

        <Box className={`link-column ${!token.isValid ? 'invalid-token' : ''}`}>
          <Link
            className={`${token.isValid ? 'link-column-link' : 'link-column-link-disabled'}`}
            href={url}
            target="_blank"
          >
            {url}
          </Link>
        </Box>

        <IconButton
          className="copy-token-button"
          color="primary"
          disabled={!token.isValid}
          onClick={() => copyToClipboard(url)}
        >
          <ContentCopyIcon />
        </IconButton>

        <IconButton className="delete-token-button" color="error" onClick={() => handleDeleteClick(token.id)}>
          <DeleteForever />
        </IconButton>
      </Box>
    );
  }

  const header = (
    <div className="shareable-links-header">
      <Typography variant="h6" data-qa-element-id="contact_details_view_title">
        {t('mainMenu.shareableLinks')}
      </Typography>
      <IconButton color="primary" onClick={handleOpenFilter}>
        <FilterAltOutlined />
      </IconButton>
      <FilterPanel
        open={open}
        anchorEl={anchorEl}
        handleCloseFilter={handleCloseFilter}
        showInactive={showInactive}
        setShowInactive={handleShowInactiveChange}
        isPhone={isPhone}
      />
    </div>
  );

  const body = (
    <>
      <Stack spacing={1.5} className="shareable-links-list">
        {(groups ?? []).map((group) => {
          return (
            <Card key={group.targetName} elevation={2} className="share-group-card">
              <Accordion disableGutters defaultExpanded={false} className="share-group-accordion">
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon className="share-group-expand-icon" />}
                  className="share-group-summary"
                >
                  <Typography variant="subtitle2">{group.targetName}</Typography>
                </AccordionSummary>

                <AccordionDetails className="share-group-details">
                  {group.targetTokens.map((token: MinimalToken, index: number) => (
                    <React.Fragment key={token.id}>
                      <ShareLinkRow token={token} t={t} />
                      {index < group.targetTokens.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </AccordionDetails>
              </Accordion>
            </Card>
          );
        })}
      </Stack>
      {groups?.length == 0 && <EmptyState isPhone={isPhone} showInactive={showInactive} />}
      <DeleteDialog
        openDeleteDialog={openDeleteDialog}
        handleCloseDialog={handleCloseDialog}
        handleConfirmDelete={handleConfirmDelete}
        isPhone={isPhone}
      />
      {showAlert && (
        <ActiveStateAlert
          showInactive={showInactive}
          setShowInactive={handleShowInactiveChange}
          onClose={handleCloseActiveStateAlert}
          isPhone={isPhone}
        />
      )}
    </>
  );

  if (isPhone) {
    return (
      <Popup
        popupTitle={undefined}
        header={header}
        body={<div className="shareable-links-container phone shareable-links-popup-body">{body}</div>}
        footer={undefined}
        closePopup={closePopup}
        additionalClassName="shareable-links-popup-content"
        headerClassName="shareable-links-popup-header"
        isFullScr
        showCloseButton={false}
        scrollUnderHeaderView
      />
    );
  }

  return (
    <div className="shareable-links-container">
      {header}
      <div className="shareable-links-desktop-body">{body}</div>
    </div>
  );
}
